'use client'
import { type FC, useState } from 'react'
import { Box, HStack, Stack } from '@chakra-ui/react'
import { GrandText } from 'ui'
import { Currency, type MarketplaceProductDTO } from 'ecosystem'
import Image from 'next/image'
import { commaSeparator } from 'shared-utils'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'

interface MarketplaceProductsItemProps {
  marketplaceProduct: MarketplaceProductDTO
}

export interface MarketplaceProductsItemOVerrides {
  currency?: string
  marketplaceProductsIsAvailable?: string
  marketplaceProductsIsNotAvailable?: string
}

const MarketplaceProductsItem: FC<MarketplaceProductsItemProps> = ({ marketplaceProduct }) => {
  const overrides = useOverridesContext<keyof MarketplaceProductsItemOVerrides>()
  const { priceMaximumFractionDigits } = useUIGlobalsContext()
  const { defaultImgSrc } = useUIGlobalsContext()
  const [imgSrc, setImgSrc] = useState(marketplaceProduct.imgUrl || defaultImgSrc)

  const handleImageError = () => {
    setImgSrc(defaultImgSrc)
  }

  const internalRedirectionUrl = `/go-to-product?channel=${marketplaceProduct.channel}&url=${marketplaceProduct.url}`

  return (
    <Stack
      as="a"
      href={internalRedirectionUrl}
      target="_blank"
      rel="noopener noreferrer"
      _hover={{
        transform: 'translate(-0.1rem)'
      }}
      cursor="pointer"
      direction={{ base: 'column' }}
      transition="all 0.07s cubic-bezier(.17,.67,.83,.67)"
      borderColor="border"
      borderWidth={1}
      rounded="md"
      p={3}
      w="full">
      <GrandText fontWeight="bold" color="gray.700" fontSize="md" flexGrow={1} isTruncated>
        {marketplaceProduct.channel}
      </GrandText>
      <HStack w="100%">
        <Box w={20} h={10} position="relative" overflow="hidden">
          <Image
            alt={marketplaceProduct.name}
            src={imgSrc}
            fill
            style={{ objectFit: 'contain' }}
            title={marketplaceProduct.name}
            onError={handleImageError}
          />
        </Box>
        <GrandText fontSize="lg" display={{ base: 'none', sm: 'initial' }} flexGrow={1} isTruncated>
          {marketplaceProduct.name}
        </GrandText>
        <GrandText
          fontSize={{
            base: 'xs',
            md: 'sm'
          }}
          fontWeight="medium"
          textAlign="right">
          {marketplaceProduct.price
            ? commaSeparator(marketplaceProduct.price, priceMaximumFractionDigits)
            : 0}{' '}
          {overrideText(Currency.SEK, overrides?.currency)}
        </GrandText>
      </HStack>
      <HStack w="full">
        <GrandText
          w="auto"
          fontSize={{
            base: 'sm',
            md: 'md'
          }}
          fontWeight="medium">
          {marketplaceProduct.inStock
            ? overrideText('Tillgänglig', overrides?.marketplaceProductsIsAvailable)
            : overrideText('Otillgänglig', overrides?.marketplaceProductsIsNotAvailable)}
        </GrandText>
      </HStack>
    </Stack>
  )
}

export default MarketplaceProductsItem
